import { useState, useEffect, useRef } from 'react';
import { Loader2, Send, Paperclip, X, ExternalLink } from 'lucide-react';
import { useSupport } from '@/contexts/SupportContext';
import { cn } from '@/lib/utils';

const STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Resolved: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500',
};

function formatTimestamp(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (isToday) return time;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

export function SupportThread() {
  const { selectedTicket, messages, loading, submitting, sendMessage } = useSupport();
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || submitting) return;

    const body = input.trim();
    const files = [...attachments];
    setInput('');
    setAttachments([]);

    try {
      await sendMessage(body, files);
    } catch (err) {
      setInput(body);
      setAttachments(files);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const isClosed = selectedTicket?.status === 'Closed';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Ticket header */}
      {selectedTicket && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            {selectedTicket.subject}
          </h3>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className={cn(
              'font-medium px-2 py-0.5 rounded-full',
              STATUS_STYLES[selectedTicket.status] || STATUS_STYLES.Open
            )}>
              {selectedTicket.status || 'Open'}
            </span>
            {selectedTicket.type && (
              <>
                <span>&middot;</span>
                <span>{selectedTicket.type}</span>
              </>
            )}
            <span>&middot;</span>
            <span>{new Date(selectedTicket.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No messages yet.</p>
        ) : (
          messages.map((msg) => {
            const isUser = msg.sender_type === 'user';
            const isSystem = msg.sender_type === 'system';

            if (isSystem) {
              return (
                <div key={msg.id} className="text-center">
                  <span className="text-xs text-gray-400 italic">{msg.body}</span>
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={cn('flex', isUser ? 'justify-end' : 'justify-start')}
              >
                <div className={cn(
                  'max-w-[80%] rounded-lg px-3 py-2',
                  isUser
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-900'
                )}>
                  {!isUser && (
                    <p className="text-xs font-medium text-gray-500 mb-1">Support</p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.body}</p>

                  {/* Attachments */}
                  {msg.attachments?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {msg.attachments.map((att, i) => (
                        <a
                          key={i}
                          href={att.url || att.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            'flex items-center gap-1 text-xs underline',
                            isUser ? 'text-white/80 hover:text-white' : 'text-primary hover:text-primary/80'
                          )}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {att.name || att.filename || `Attachment ${i + 1}`}
                        </a>
                      ))}
                    </div>
                  )}

                  <p className={cn(
                    'text-xs mt-1',
                    isUser ? 'text-white/60' : 'text-gray-400'
                  )}>
                    {formatTimestamp(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply input */}
      {!isClosed && (
        <div className="border-t border-gray-200">
          {/* Attachment preview */}
          {attachments.length > 0 && (
            <div className="px-4 pt-2 flex flex-wrap gap-1">
              {attachments.map((file, i) => (
                <div key={i} className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 rounded px-2 py-1">
                  <Paperclip className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate max-w-[120px]">{file.name}</span>
                  <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-gray-200 rounded">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSend} className="flex items-end gap-2 p-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Paperclip className="h-5 w-5" />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            <button
              type="submit"
              disabled={!input.trim() || submitting}
              className="p-2 text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </form>
        </div>
      )}

      {isClosed && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-center">
          <p className="text-sm text-gray-500">This request has been closed.</p>
        </div>
      )}
    </div>
  );
}
